/** Strip Markdown syntax and keep the words: the logic behind the Markdown to Text tool. */

/* ------------------------------------------------------------------ */
/*  Regex constants (module level per Biome rule)                     */
/* ------------------------------------------------------------------ */

const RE_FENCED_CODE_BLOCK = /```[\s\S]*?```/g;
const RE_HEADERS = /^#{1,6}[ \t]+/gm;
// Emphasis markers only pair around text that starts and ends with a non-space,
// as in CommonMark, so a spaced `2 * 3 * 4` is left alone.
const RE_BOLD_ASTERISK = /\*\*(?=\S)(.+?)(?<=\S)\*\*/g;
const RE_BOLD_UNDERSCORE = /(?<!\w)__(?=\S)(.+?)(?<=\S)__(?!\w)/g;
const RE_ITALIC_ASTERISK = /\*(?![\s*])(.+?)(?<![\s*])\*/g;
const RE_ITALIC_UNDERSCORE = /(?<!\w)_(?=\S)(.+?)(?<=\S)_(?!\w)/g;
const RE_STRIKETHROUGH = /~~(?=\S)(.+?)(?<=\S)~~/g;
const RE_INLINE_CODE = /`([^`]+)`/g;
const RE_IMAGES = /!\[([^\]]*)\]\([^)]+\)/g;
const RE_LINKS = /\[([^\]]+)\]\([^)]+\)/g;
const RE_REFERENCE_LINKS = /\[([^\]]+)\]\[[^\]]*\]/g;
const RE_REFERENCE_DEFS = /^[ \t]*\[[^\]]+\]:[ \t]+.+$/gm;
const RE_BLOCKQUOTES = /^>[ \t]?/gm;
// Horizontal spaces only: `\s` would also swallow the blank line before the list.
const RE_UNORDERED_LIST = /^[ \t]*[-*+][ \t]+/gm;
const RE_ORDERED_LIST = /^[ \t]*\d+\.[ \t]+/gm;
const RE_HORIZONTAL_RULE = /^[-*_]{3,}[ \t]*$/gm;
const RE_MULTIPLE_BLANK_LINES = /\n{3,}/g;

/* ------------------------------------------------------------------ */
/*  Markdown stripping logic                                          */
/* ------------------------------------------------------------------ */

export interface ConvertResult {
  output: string;
  inputChars: number;
  outputChars: number;
  inputLines: number;
  outputLines: number;
  elementsRemoved: number;
}

export function stripMarkdown(md: string): ConvertResult | null {
  const trimmed = md.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const inputChars = trimmed.length;
  const inputLines = trimmed.split("\n").length;
  let elementsRemoved = 0;

  function countAndReplace(
    text: string,
    regex: RegExp,
    replacement: string
  ): string {
    const matches = text.match(regex);
    if (matches) {
      elementsRemoved += matches.length;
    }
    return text.replace(regex, replacement);
  }

  let result = trimmed;

  // Remove fenced code blocks (keep content)
  result = result.replace(RE_FENCED_CODE_BLOCK, (match) => {
    elementsRemoved++;
    const lines = match.split("\n");
    // Remove first and last lines (the ``` delimiters)
    return lines.slice(1, -1).join("\n");
  });

  // Remove headers
  result = countAndReplace(result, RE_HEADERS, "");

  // Remove images (keep alt text)
  result = countAndReplace(result, RE_IMAGES, "$1");

  // Remove links (keep text)
  result = countAndReplace(result, RE_LINKS, "$1");
  result = countAndReplace(result, RE_REFERENCE_LINKS, "$1");
  result = countAndReplace(result, RE_REFERENCE_DEFS, "");

  // Remove bold
  result = countAndReplace(result, RE_BOLD_ASTERISK, "$1");
  result = countAndReplace(result, RE_BOLD_UNDERSCORE, "$1");

  // Remove italic
  result = countAndReplace(result, RE_ITALIC_ASTERISK, "$1");
  result = countAndReplace(result, RE_ITALIC_UNDERSCORE, "$1");

  // Remove strikethrough
  result = countAndReplace(result, RE_STRIKETHROUGH, "$1");

  // Remove inline code
  result = countAndReplace(result, RE_INLINE_CODE, "$1");

  // Remove blockquotes
  result = countAndReplace(result, RE_BLOCKQUOTES, "");

  // Remove list markers
  result = countAndReplace(result, RE_UNORDERED_LIST, "");
  result = countAndReplace(result, RE_ORDERED_LIST, "");

  // Remove horizontal rules
  result = countAndReplace(result, RE_HORIZONTAL_RULE, "");

  // Clean up extra blank lines
  result = result.replace(RE_MULTIPLE_BLANK_LINES, "\n\n").trim();

  const outputChars = result.length;
  const outputLines = result.split("\n").length;

  return {
    output: result,
    inputChars,
    outputChars,
    inputLines,
    outputLines,
    elementsRemoved,
  };
}
